import React from "react";
import ArticleLayout, { ArticleH2, Cite } from "../../components/ArticleLayout";
import { usePageMeta } from "../../hooks/usePageMeta";

const SECTIONS = [
    { id: "what-is-bpc157", label: "What BPC-157 is" },
    { id: "why-study", label: "Why researchers study it" },
    { id: "gi-research", label: "Gastrointestinal research" },
    { id: "musculoskeletal", label: "Tendon, ligament & muscle" },
    { id: "angiogenesis", label: "Angiogenesis & VEGF" },
    { id: "mechanisms", label: "Proposed mechanisms" },
    { id: "limitations", label: "Limitations of the evidence" },
    { id: "human-evidence", label: "Human evidence" },
    { id: "references", label: "References" },
];

const REFERENCES = [
    {
        n: 1,
        text:
            "Sikiric P, Seiwerth S, Rucman R, et al. Stable gastric pentadecapeptide BPC 157: novel therapy in gastrointestinal tract. Curr Pharm Des. 2011;17(16):1612-1632.",
        url: "https://pubmed.ncbi.nlm.nih.gov/21548867/",
    },
    {
        n: 2,
        text:
            "Sikiric P, Seiwerth S, Rucman R, et al. Toxicity by NSAIDs: Counteraction by stable gastric pentadecapeptide BPC 157. Curr Pharm Des. 2013;19(1):76-83.",
        url: "https://pubmed.ncbi.nlm.nih.gov/22950504/",
    },
    {
        n: 3,
        text:
            "Chang CH, Tsai WC, Lin MS, Hsu YH, Pang JHS. The promoting effect of pentadecapeptide BPC 157 on tendon healing involves tendon outgrowth, cell survival, and cell migration. J Appl Physiol. 2011;110(3):774-780.",
        url: "https://pubmed.ncbi.nlm.nih.gov/21030672/",
    },
    {
        n: 4,
        text:
            "Krivic A, Anic T, Seiwerth S, Huljev D, Sikiric P. Achilles detachment in rat and stable gastric pentadecapeptide BPC 157: Promoted tendon-to-bone healing and opposed corticosteroid aggravation. J Orthop Res. 2006;24(5):982-989.",
        url: "https://pubmed.ncbi.nlm.nih.gov/16583445/",
    },
    {
        n: 5,
        text:
            "Staresinic M, Petrovic I, Novinscak T, et al. Effective therapy of transected quadriceps muscle in rat: gastric pentadecapeptide BPC 157. J Orthop Res. 2006;24(5):1109-1117.",
        url: "https://pubmed.ncbi.nlm.nih.gov/16609963/",
    },
    {
        n: 6,
        text:
            "Brcic L, Brcic I, Staresinic M, Novinscak T, Sikiric P, Seiwerth S. Modulatory effect of gastric pentadecapeptide BPC 157 on angiogenesis in muscle and tendon healing. J Physiol Pharmacol. 2009;60 Suppl 7:191-196.",
        url: "https://pubmed.ncbi.nlm.nih.gov/20388063/",
    },
    {
        n: 7,
        text:
            "Hsieh MJ, Liu HT, Wang CN, et al. Therapeutic potential of pro-angiogenic BPC157 is associated with VEGFR2 activation and upregulation. J Mol Med (Berl). 2017;95(3):323-333.",
        url: "https://pubmed.ncbi.nlm.nih.gov/27847966/",
    },
    {
        n: 8,
        text:
            "Sikiric P, Rucman R, Turkovic B, et al. Novel Cytoprotective Mediator, Stable Gastric Pentadecapeptide BPC 157. Vascular Recruitment and Gastrointestinal Tract Healing. Curr Pharm Des. 2018;24(18):1990-2001.",
        url: "https://pubmed.ncbi.nlm.nih.gov/29769003/",
    },
    {
        n: 9,
        text:
            "Seiwerth S, Milavic M, Vukojevic J, et al. Stable Gastric Pentadecapeptide BPC 157 and Wound Healing. Front Pharmacol. 2021;12:627533.",
        url: "https://pubmed.ncbi.nlm.nih.gov/34267654/",
    },
    {
        n: 10,
        text:
            "Sikiric P, Hahm KB, Blagaic AB, et al. Stable Gastric Pentadecapeptide BPC 157, Robert's Stomach Cytoprotection/Adaptive Cytoprotection/Organoprotection, and Selye's Stress Coping Response: Progress, Achievements, and the Future. Gut Liver. 2020;14(2):153-167.",
        url: "https://pubmed.ncbi.nlm.nih.gov/31158953/",
    },
    {
        n: 11,
        text:
            "Chang CH, Tsai WC, Hsu YH, Pang JHS. Pentadecapeptide BPC 157 enhances the growth hormone receptor expression in tendon fibroblasts. Molecules. 2014;19(11):19066-19077.",
        url: "https://pubmed.ncbi.nlm.nih.gov/25415472/",
    },
];

export const BPC157Research = () => {
    usePageMeta({
        title: "BPC-157 Research: What Preclinical Studies Have Investigated — Quantum Genesis Labs",
        description:
            "An educational overview of BPC-157 preclinical research: gastrointestinal, tendon and ligament, angiogenesis and VEGF studies, proposed mechanisms, and current limitations of the evidence. For Research Use Only.",
        path: "/research/bpc-157-research",
    });

    return (
        <ArticleLayout
            title="BPC-157 Research: What Preclinical Studies Have Investigated"
            category="Peptide Research"
            readTime="12 min read"
            publishedAt="February 2026"
            sections={SECTIONS}
            references={REFERENCES}
        >
            <p className="text-[16px] sm:text-[16.5px] text-slate-300 leading-[1.85]">
                BPC-157 (Body Protection Compound-157) is a synthetic 15-amino-acid peptide
                derived from a partial sequence of a protein originally identified in human
                gastric juice. Over the past three decades, laboratory investigators have used
                BPC-157 as a research tool to probe tissue-repair biology across a range of
                animal models. This article summarizes what preclinical research has actually
                examined — and, importantly, what it has <em>not</em> yet established.
            </p>

            <ArticleH2 id="what-is-bpc157">What BPC-157 is</ArticleH2>
            <p>
                BPC-157 is the abbreviation for a stable pentadecapeptide (a 15-amino-acid
                fragment) with the sequence GEPPPGKPADDAGLV. Researchers isolated the parent
                Body Protection Compound from human gastric juice, and the 15-residue fragment
                was subsequently synthesized for laboratory investigation.<Cite n={1} /> Unlike
                many peptides used in research, BPC-157 has been reported to remain stable in
                gastric fluid, which is one reason it has been widely studied in models of
                gastrointestinal injury.<Cite n={1} />
            </p>
            <p>
                In the published literature, BPC-157 is consistently referred to as a
                <em> pentadecapeptide</em>, a <em> gastric peptide</em>, or a
                <em> cytoprotective mediator</em>. It is not a hormone, not a receptor
                ligand of a single well-defined pathway, and not an approved therapeutic in
                any jurisdiction that we are aware of at the time of writing.
            </p>

            <ArticleH2 id="why-study">Why researchers study it</ArticleH2>
            <p>
                Early experiments suggested BPC-157 could modulate healing responses across
                several tissue types, which sparked broader interest in the compound as a
                model for studying <em>cytoprotection</em> — the concept that certain
                molecules may help tissue withstand or recover from injury.<Cite n={10} /> Rather
                than a single therapeutic target, BPC-157 has been used to probe overlapping
                biological systems: the gastrointestinal tract, connective tissue repair,
                vascular growth, and inflammatory signaling.
            </p>
            <p>
                For a researcher, BPC-157&apos;s appeal lies in its stability, its reproducible
                effects across published animal models, and the breadth of endpoints
                (histology, biomechanics, tensile strength, angiogenic markers) that can be
                measured in the same experimental system.
            </p>

            <ArticleH2 id="gi-research">Gastrointestinal research</ArticleH2>
            <p>
                The largest single body of BPC-157 literature concerns the gastrointestinal
                (GI) tract, particularly in rodent models. Studies have examined outcomes such
                as gastric ulcer healing, protection against non-steroidal anti-inflammatory
                drug (NSAID) injury, and colitis-model endpoints.<Cite n={1} /><Cite n={2} />
            </p>
            <p>
                In one representative line of work, Sikiric and colleagues reported that
                BPC-157 counteracted several outcomes of NSAID exposure in rats, including
                lesions in the stomach and small intestine.<Cite n={2} /> These are <strong>animal
                studies</strong>, and the endpoints — lesion counts, histology, tissue markers —
                do not translate directly into clinical claims. They are best understood as
                mechanistic starting points that would need substantial further work
                (dose–response, pharmacokinetics, controlled human trials) before any human
                relevance could be evaluated.
            </p>

            <ArticleH2 id="musculoskeletal">Tendon, ligament and muscle research</ArticleH2>
            <p>
                A second cluster of studies investigates BPC-157 in musculoskeletal repair
                models — again, predominantly in rats. Chang et al. reported effects on
                tendon fibroblast outgrowth, cell survival, and cell migration in an
                <em> in-vitro</em> model, providing a plausible mechanism by which the peptide
                might participate in tendon-healing pathways.<Cite n={3} />
            </p>
            <p>
                Complementary <em>in-vivo</em> work in rats has examined Achilles detachment
                and tendon-to-bone healing,<Cite n={4} /> transected quadriceps muscle,
                <Cite n={5} /> and — separately — reported effects on growth-hormone-receptor
                expression in tendon fibroblasts.<Cite n={11} /> Across this literature the
                consistent framing is that BPC-157 modulates the <em>rate</em> or
                <em> quality</em> of tissue repair in animal models. Whether comparable
                effects occur in humans has not been established in adequately powered
                clinical trials.
            </p>

            <ArticleH2 id="angiogenesis">Angiogenesis and VEGF-related research</ArticleH2>
            <p>
                One of the mechanistic threads that ties the GI and musculoskeletal
                literatures together is angiogenesis — the formation of new blood vessels.
                Brcic et al. described a modulatory effect of BPC-157 on angiogenesis in
                muscle and tendon healing in rats.<Cite n={6} /> Hsieh et al. subsequently
                reported that BPC-157&apos;s pro-angiogenic effects were associated with activation
                and up-regulation of VEGFR2, a receptor central to vascular endothelial growth
                factor (VEGF) signaling.<Cite n={7} />
            </p>
            <p>
                In more recent reviews, Sikiric and colleagues have proposed BPC-157 as a
                vascular-recruiting cytoprotective agent, linking its various reported
                effects to the recruitment and reorganization of blood vessels around
                injured tissue.<Cite n={8} />
            </p>

            <ArticleH2 id="mechanisms">Proposed biological mechanisms</ArticleH2>
            <p>
                Based on the animal and in-vitro literature, several overlapping mechanisms
                have been <em>proposed</em>. It is important to note that these are
                hypotheses derived from preclinical work — not confirmed pathways in humans.
            </p>
            <ul className="list-disc pl-6 space-y-2 my-4 text-slate-300">
                <li>
                    <strong>Angiogenic modulation.</strong> Interaction with the
                    VEGF/VEGFR2 axis and downstream endothelial signaling.<Cite n={7} />
                </li>
                <li>
                    <strong>Nitric-oxide (NO) system involvement.</strong> Studies have
                    reported changes in NO-related endpoints and interactions with
                    NO-synthase inhibitors and NO donors.<Cite n={10} />
                </li>
                <li>
                    <strong>Growth-factor and growth-hormone-receptor signaling.</strong>
                    {" "}Reported effects on growth-hormone receptor expression in tendon
                    fibroblasts.<Cite n={11} />
                </li>
                <li>
                    <strong>Cytoprotection framework.</strong> BPC-157 is often discussed
                    within Selye&apos;s stress-coping and Robert&apos;s cytoprotection theories as a
                    tissue-adaptive mediator.<Cite n={10} />
                </li>
                <li>
                    <strong>Wound-healing signaling.</strong> A 2021 review summarized
                    animal data suggesting effects on multiple wound-healing pathways
                    including collagen deposition and inflammatory resolution.<Cite n={9} />
                </li>
            </ul>

            <ArticleH2 id="limitations">Current limitations of the evidence</ArticleH2>
            <p>
                The preclinical literature on BPC-157 is broad, but there are meaningful
                limitations that researchers reading it should keep in mind:
            </p>
            <ul className="list-disc pl-6 space-y-2 my-4 text-slate-300">
                <li>
                    <strong>Predominantly rodent-based.</strong> The vast majority of
                    published outcomes come from rat models, with a smaller in-vitro literature.
                    Rodent tissue-repair biology does not automatically extrapolate to humans.
                </li>
                <li>
                    <strong>Concentrated authorship.</strong> A large fraction of published
                    studies originate from a small number of research groups, which is
                    common in emerging peptide fields but limits independent replication.
                </li>
                <li>
                    <strong>Sparse pharmacokinetic data.</strong> Systematic absorption,
                    distribution, metabolism, and excretion data in humans are largely
                    absent from the peer-reviewed literature.
                </li>
                <li>
                    <strong>Heterogeneous methods.</strong> Routes of administration,
                    dosing, and endpoints vary across studies, making direct comparison
                    across the literature difficult.
                </li>
                <li>
                    <strong>Regulatory status.</strong> BPC-157 is not an approved medicine
                    in any major jurisdiction. It is prohibited by the World Anti-Doping
                    Agency (WADA) for use in sport under the S0 category of unapproved substances.
                </li>
            </ul>

            <ArticleH2 id="human-evidence">Human evidence remains very limited</ArticleH2>
            <p>
                We want to be explicit: <strong>as of publication, there are no
                large, well-controlled, peer-reviewed randomized clinical trials in humans
                that establish safety or efficacy for BPC-157 in any indication.</strong> The
                published literature is overwhelmingly preclinical — animal and in-vitro.
                Any promotional material that presents rodent healing results as evidence of
                human benefit is misrepresenting the state of the science.
            </p>
            <p>
                For researchers, the correct framing is that BPC-157 is an
                <em> investigational research compound</em> with an active preclinical
                literature, promising mechanistic hypotheses (particularly around
                angiogenesis and cytoprotection), and open questions about pharmacokinetics,
                dose–response, and human translation that would need substantial further
                work to answer.
            </p>
            <p className="text-slate-400 italic mt-8">
                This article summarizes published scientific research and is provided for
                informational purposes only. Nothing above should be interpreted as a
                dosing guideline, treatment recommendation, or medical advice. Products
                sold by Quantum Genesis Labs are for laboratory research use only and are
                not intended for human consumption, veterinary use, or clinical purposes.
            </p>
        </ArticleLayout>
    );
};

export default BPC157Research;
